// Author: Çağrı Erdem, 2024
// Description: data recording script for the dB study

// TODO: add tapping and generated groove arrays to the recorded data

import { globalFetch } from './globalFetch.js';
import { getBpm, getTappedRhythms, participantID, sessionID } from './interface.js'; // TODO: Uncomment before deployment


let isRecording = false;
let recordedData = [];  // To store all recorded data
let batchData = [];     // To store data in batches
let keyboardData = [];  // To capture keyboard events
const batchSize = 100;  // Number of frames per batch

let frameCounter = 0;
const sampleRate = 3;  // 3 iste 10fps'ye denk geliyor (30'u bolecen) --5'e kadar yolu var 
const decPts = 5;  // Number of decimal points to round the data
//TODO: eger ana capture'da dusuk fps kullanirsak, kayitta ne kullancagimizi test etmek lazim.

let lastRecordedTempo = null;
let lastTapData = null;
let lastMidiData = null;


//////////////////////////
////////////////////////// Keyboard Interaction 
////////////////////////// 

// Capture 'keydown' events
window.addEventListener('keydown', (event) => {
    const keyInteraction = {
        key: event.key,
        eventType: 'keydown',
        timestamp: Date.now()
    };
    keyboardData.push(keyInteraction);
    // console.log('Key down:', keyInteraction); // TODO: Comment out when needed
});

// Capture 'keyup' events
window.addEventListener('keyup', (event) => {
    const keyInteraction = {
        key: event.key,
        eventType: 'keyup',
        timestamp: Date.now()
    };
    keyboardData.push(keyInteraction);
    // console.log('Key up:', keyInteraction); 
});


// Request file permission when starting the recording
async function requestFilePermission() {
    //TODO: implement it.
    const options = {
        types: [{
            description: 'JSON Files',
            accept: { 'application/json': ['.json'] }
        }]
    };

    try {
        // Request the file handle (this will trigger a prompt)
        fileHandle = await window.showSaveFilePicker(options);
        writableStream = await fileHandle.createWritable();
        console.log('Permission granted for file saving.');
    } catch (err) {
        console.error('Error requesting file permission:', err);
    }
}



// Function to capture data for recording with batch processing
export function captureAndProcessData(faceLandmarks, poseLandmarks, blendShapes, orientation) {
    if (isRecording) {
        // // Uncomment to skip frames based on sample rate but not needed since we reduced the landmarks
        // frameCounter++;
        // if (frameCounter % sampleRate !== 0) {
        //     // Skip this frame
        //     return;
        // }

        // // Reset frame counter if it gets too large
        // if (frameCounter >= 10000) {
        //     frameCounter = 0;
        // }

        const timestamp = performance.now();

        // Capture face landmarks if they exist, otherwise return an empty array
        const processedFaceLandmarks = faceLandmarks ? faceLandmarks.map((landmark) => ({
            x: landmark.x ? landmark.x.toFixed(decPts) : 0,
            y: landmark.y ? landmark.y.toFixed(decPts) : 0,
            z: landmark.z ? landmark.z.toFixed(decPts) : 0,
            part: landmark.part || "unknown"  // Use the actual 'part' value from each landmark
        })) : [];

        // Capture pose landmarks if they exist, otherwise return an empty array
        const processedPoseLandmarks = poseLandmarks ? poseLandmarks.map((landmark) => ({
            x: landmark.x ? landmark.x.toFixed(decPts) : 0,
            y: landmark.y ? landmark.y.toFixed(decPts) : 0,
            z: landmark.z ? landmark.z.toFixed(decPts) : 0,
            part: landmark.part || "unknown"  // Use the actual 'part' value from each landmark
        })) : [];

        const currentBpm = getBpm(); // Get the current BPM value
        const currentTapData = getTappedRhythms();  // Get the current tapping data
        const currentMidiData = globalFetch.getMidiData();  // Get the current MIDI data

        // Capture blend shapes if they exist, otherwise return an empty array
        let processedBlendShapes = [];
        if (blendShapes && typeof blendShapes === 'object') {
            try {
                for (const [categoryName, score] of Object.entries(blendShapes)) {
                    if (typeof score === 'number') {
                        processedBlendShapes.push({
                            categoryName,
                            score: parseFloat(score.toFixed(decPts))
                        });
                    } else {
                        console.warn(`Invalid score value for blend shape ${categoryName}. Expected a number, got:`, score);
                        processedBlendShapes.push({
                            categoryName,
                            score: 0.0 // Default to 0.0 if score is not a number
                        });
                    }
                }
            } catch (error) {
                console.error("Error processing blend shapes:", error);
            }
        } else {
            console.warn("Blend shapes are missing or empty.");
        }

        // Capture the keyboard interactions and reset the keyboard data
        const processedKeyboardInteractions = [...keyboardData];
        keyboardData = [];  // Clear keyboard data after processing

        // Capture tempo (BPM) only if it has changed since the last recorded value
        let processedTempo = null;
        if (currentBpm !== lastRecordedTempo) {
            processedTempo = currentBpm;
            lastRecordedTempo = currentBpm;  // Update last recorded tempo
        }

        // Capture tapping data if it has changed since the last recorded value
        let tapData = null;
        if (currentTapData !== lastTapData) {
            tapData = currentTapData;
            lastTapData = currentTapData;  // Update last recorded tapping data
        }

        // Capture MIDI data if it has changed since the last recorded value
        let midiData = null;
        if (currentMidiData !== lastMidiData) {
            midiData = currentMidiData;
            lastMidiData = currentMidiData;  // Update last recorded MIDI data  
        }

        // Collect all the data into a frame
        const frameData = {
            participantID,
            sessionID,
            timestamp,
            faceLandmarks: processedFaceLandmarks,
            poseLandmarks: processedPoseLandmarks,
            blendShapes: processedBlendShapes,
            headOrientation: orientation,
            keyboardInteractions: processedKeyboardInteractions,
        };

        // Add tempo to the frame data only if it's been changed
        if (processedTempo !== null) {
            console.log(`[Recording] Tempo changed to ${processedTempo} BPM`);
            frameData.tempo = processedTempo;
        }

        // Add tapping data to the frame data only if it's been changed
        if (tapData !== null) {
            console.log(`[Recording] Tapping data updated:`, tapData);
            frameData.tapping = tapData;
        }

        // Add MIDI data to the frame data only if it's been changed
        if (midiData !== null) {
            console.log(`[Recording] MIDI data updated:`, midiData);
            frameData.groove = midiData;
        }

        // Add the frame data to the current batch and the overall recorded data
        batchData.push(frameData);
        recordedData.push(frameData);  // Ensure data is added to recordedData as well

        // If batch size is reached, save the batch and clear batchData
        if (batchData.length >= batchSize) {
            saveBatch(batchData);
            batchData = [];  // Reset batch after saving
        }
    }
}


// Function to save a batch of data (local file download as example)
function saveBatch(data) {
    const jsonData = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `batch_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log('Batch saved:', data);
}

// Function to stop recording and save remaining data
export function toggleRecording() {
    isRecording = !isRecording;
    
    if (!isRecording) {
        // Save any remaining unsaved data in batch
        if (batchData.length > 0) {
            saveBatch(batchData);
            batchData = [];  // Reset batch data after saving
        }
    }
    
    return isRecording;
}

// Using the File System Access API to save all recorded data --asks for permission
export async function saveRecordedData() {
    const options = {
        types: [{
            description: 'JSON Files',
            accept: { 'application/json': ['.json'] }
        }]
    };

    try {
        // Open a file picker to save the file
        const fileHandle = await window.showSaveFilePicker(options);
        const writableStream = await fileHandle.createWritable();

        // Write all recorded data including remaining batch
        const allData = recordedData.concat(batchData);  // Combine recorded data and any remaining batch
        await writableStream.write(JSON.stringify(allData, null, 2));
        await writableStream.close();

        // Clear recordedData and batchData after saving
        recordedData = [];
        batchData = [];

        console.log('All recorded data saved successfully.');
    } catch (err) {
        console.error('Error saving file:', err);
    }
}


function generateFileName(participantID, sessionID) {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // Add 1 because getMonth() is 0-based
    const day = String(currentDate.getDate()).padStart(2, '0');
    const dateString = `${year}${month}${day}`;

    return `IN5060_${participantID}_${sessionID}_${dateString}.json`;
}

function getCurrentTime() {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0'); // Get hours and pad with leading zeros
    const minutes = String(now.getMinutes()).padStart(2, '0'); // Get minutes and pad
    const seconds = String(now.getSeconds()).padStart(2, '0'); // Get seconds and pad

    return `${hours}${minutes}${seconds}`; // Format as HHMMSS
}


const dateString = new Date().toISOString().split('T')[0]; // Get the current date as YYYY-MM-DD


// Function to save all recorded data to the downloads folder directly
export async function saveDataToFile(participantID, sessionID) {
    try {
        // Generate the filename
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // Add 1 because getMonth() is 0-based
        const day = String(currentDate.getDate()).padStart(2, '0');
        const dateString = `${year}${month}${day}`;

        const fileName = `IN5060_${participantID}_${sessionID}_${dateString}_${getCurrentTime()}.json`;

        // Combine recorded data and any remaining batch
        const allData = recordedData.concat(batchData);  

        // Create a JSON Blob
        const jsonString = JSON.stringify(allData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });

        // Create a link element to trigger download
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;

        // Programmatically click the link to trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        // Clear recordedData and batchData after saving
        recordedData = [];
        batchData = [];

        console.log('All recorded data saved successfully.');
    } catch (err) {
        console.error('Error saving file:', err);
    }
}